import { type PortLicenseRequestFormData } from "@features/licensing/license-details/flow/request-port-operator-license/schema/portOperatorLicenseRequestSchema";
import type { LicenseResponse } from "@services/licensing/types";
import { DocumentTypes } from "@services/licensing/types";
import type { UserResponse } from "@services/user/types";

const appendExtensionFromUrl = (baseName: string, url?: string) => {
  if (!url) return baseName;

  const cleanUrl = url.split("?")[0].split("#")[0];
  const lastDotIndex = cleanUrl.lastIndexOf(".");
  if (lastDotIndex === -1) return baseName;

  const extension = cleanUrl.substring(lastDotIndex + 1);
  return extension ? `${baseName}.${extension}` : baseName;
};

export const getTradeLicenseUploadEntries = (
  license: Pick<LicenseResponse, "documents" | "status" | "taskDecision"> | null | undefined,
) => {
  if (!license) return [];
  if (!isApprovedVendorLicense(license)) return [];

  const tradeDoc = license.documents?.find((d) => d.documentType === DocumentTypes.TRADE_LICENSE);

  return tradeDoc?.documentUrl
    ? [
        {
          name:
            tradeDoc.originalFileName ??
            appendExtensionFromUrl("trade-license", tradeDoc.documentUrl),
          url: tradeDoc.documentUrl,
          type: tradeDoc.documentType,
        },
      ]
    : [];
};

export const isApprovedVendorLicense = (
  license: Pick<LicenseResponse, "status" | "taskDecision"> | null | undefined,
) => {
  if (!license) return false;

  if (license.status && license.status !== "APPROVED") return false;
  if (license.taskDecision && license.taskDecision.status !== "APPROVED") return false;

  return Boolean(license.status || license.taskDecision);
};

export const getOtherDocumentUploadEntries = (
  license: Pick<LicenseResponse, "documents"> | null | undefined,
) => {
  if (!license?.documents?.length) return [];

  return license.documents
    .filter((document) => document.documentType === DocumentTypes.OTHER_DOCUMENT)
    .map((document, index) => ({
      name:
        document.originalFileName ??
        appendExtensionFromUrl(`other-document-${index + 1}`, document.documentUrl),
      url: document.documentUrl ?? "",
      type: document.documentType,
    }))
    .filter((document) => Boolean(document.url));
};

function mapUserRoles(roles: string[]): string {
  return (
    roles
      .filter((role) => ["vendor-master", "licensed-company-agent"].includes(role))
      .map((role) => (role === "vendor-master" ? "Company Agent" : "Licensed Agent"))[0] ?? ""
  );
}

function getApplicantDetails(x: LicenseResponse, user: UserResponse | null) {
  if (!user) {
    return {
      applicantEmail: x.applicantEmail ?? "",
      applicantName: x.applicantName ?? "",
      applicantPosition: x.applicantPosition ?? "",
    };
  }

  return {
    applicantEmail: user.email ?? "",
    applicantName: user.name ?? "",
    applicantPosition: mapUserRoles(user.roles ?? []),
  };
}

export const mapLicenseToPortForm = (
  x: LicenseResponse,
  user: UserResponse | null,
): PortLicenseRequestFormData => {
  const applicantDetails = getApplicantDetails(x, user);

  return {
    companyName: x.companyName ?? "",
    companyNameArabic: x.companyNameArabic ?? "",
    companyTypes: Array.isArray(x.companyTypes)
      ? x.companyTypes.map(({ companyTypeCode }) => companyTypeCode)
      : x.companyTypes
        ? x.companyTypes
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],

    companyAddress: x.companyAddress ?? "",
    telephoneNumber: x.telephoneNumber ?? "",
    mobileNumber: x.mobileNumber ?? "",
    faxNumber: String(x.faxNumber ?? ""),

    companyEmail: x.companyEmail ?? "",

    applicantEmail: applicantDetails.applicantEmail,
    applicantName: applicantDetails.applicantName,
    applicantPosition: applicantDetails.applicantPosition,

    natureOfBusinessInPof: x.natureOfBusinessInPof ?? "",

    numberOfEmployees: x.numberOfEmployees != null ? String(x.numberOfEmployees) : "",
    numberOfVehicles: x.numberOfVehicles != null ? String(x.numberOfVehicles) : "",

    tradingLicenseNumber: x.tradingLicenseNumber ?? "",
    tradingLicenseValidityDate: x.tradingLicenseValidityDate ?? "",

    tradeLicenseIssuingAuthority: x.tradeLicenseIssuingAuthority ?? "",
    purposeOfRequest: x.purposeOfRequest ?? "",

    tradeLicenseDocumentUrl: getTradeLicenseUploadEntries(x),

    othersDocumentUrl: getOtherDocumentUploadEntries(x),
    fujairahRegisteredCompany: true,
  };
};

export const mapCurrentVendorLicenseToPortForm = mapLicenseToPortForm;
